import dayjs from 'dayjs';

import { intl } from '~lib/intl';
import { findAndUpdate } from '~utils/array';

export const modifiersMapper = (items, mapper) => Object.values(items).flat().map(mapper);

export const getFinalPrice = (item) => item?.discountPrice || item?.price;

export const priceItem = (items, count, basePrice) => {
  let totalModifiersPrice = 0;

  // Proteger contra items undefined o null
  if (!items || typeof items !== 'object') {
    items = {};
  }

  // Procesar cada grupo de modificadores
  Object.keys(items).forEach((groupId) => {
    const selected = items[groupId];

    if (Array.isArray(selected)) {
      // Checkbox/Stepper - array de objetos con quantity
      selected.forEach((mod) => {
        const quantity = mod.quantity || 1;
        const price = mod.price || 0;
        totalModifiersPrice += price * quantity;
      });
    } else if (selected && typeof selected === 'object') {
      // Objeto individual
      const price = selected.price || 0;
      const quantity = selected.quantity || 1;
      totalModifiersPrice += price * quantity;
    }
  });

  const totalPrice = count * (basePrice + totalModifiersPrice);

  return totalPrice;
};

export const priceCart = (cart) => {
  const cartArray = cart.cart || cart;

  if (!Array.isArray(cartArray)) {
    console.warn('priceCart: cart is not an array', cart);
    return 0;
  }
  return cartArray.reduce((accum, item) => accum + priceItem(item.items, item.count, item.basePrice), 0);
};

// creates unique hash for each item - modifier combination
export const getHash = (item) => {
  const productId = item.product?.id || item.product?.productId || item.productId || item.id || 'noid';
  const items = item.items || {};

  // If it comes as a flat modifiers array, normalize without relying on indices
  if (Array.isArray(items)) {
    const modsArray = items.asMutable ? items.asMutable() : [...items];
    const normalized = modsArray
      .map((mod) => ({
        group: mod.modifierId || mod.parentId || 'g',
        id: mod.id,
        quantity: Number(mod.quantity || 1)
      }))
      .sort((a, b) => (a.group > b.group ? 1 : a.group < b.group ? -1 : (a.id || 0) - (b.id || 0)))
      .map((m) => `${m.group}-${m.id}-${m.quantity}`)
      .join('|');
    return `${productId}-${normalized}`;
  }

  // If it comes as a grouped object, normalize as well
  const modifiersString = Object.keys(items)
    .sort()
    .map((groupKey) => {
      const mods = items[groupKey];
      if (Array.isArray(mods)) {
        const arr = mods.asMutable ? mods.asMutable() : [...mods];
        return arr
          .sort((a, b) => (a.id || 0) - (b.id || 0))
          .map((mod) => `${groupKey}-${mod.id}-${Number(mod.quantity || 1)}`)
          .join(',');
      }
      if (mods && typeof mods === 'object') {
        return `${groupKey}-${mods.id}-${Number(mods.quantity || 1)}`;
      }
      return '';
    })
    .filter(Boolean)
    .join('|');

  return `${productId}-${modifiersString}`;
};

export const addItemToCart = (item) => (state) => {
  const { cart } = state;

  // Convertir a array mutable si es necesario
  const cartArray = cart.asMutable ? cart.asMutable() : Array.isArray(cart) ? cart : [];

  if (!Array.isArray(cartArray)) {
    console.warn('addItemToCart: cart is not an array, using empty array', cart);
    return { cart: [{ ...item, hash: getHash(item) }], state };
  }

  const hash = getHash(item);

  const [newCart, existingItem] = findAndUpdate(
    (i) => {
      const itemHash = i.hash || getHash(i);
      return itemHash === hash;
    },
    (i) => ({ ...i, count: i.count + item.count }),
    cartArray
  );

  const result = { cart: existingItem ? newCart : [...cartArray, { ...item, hash }], state };

  return result;
};

export const sumItemToCart = (item) => (state) => {
  const { cart } = state;

  // Asegurar que cart sea un array
  if (!Array.isArray(cart)) {
    console.warn('sumItemToCart: cart is not an array, using empty array', cart);
    return { cart: [{ ...item, hash: getHash(item) }], state };
  }

  const hash = getHash(item);
  const [newCart, existingItem] = findAndUpdate(
    (i) => i.hash === hash,
    (i) => ({ ...i, quantity: i.quantity + 1 }),
    cart
  );
  return { cart: existingItem ? newCart : [...newCart, { ...item, hash }], state };
};

export const removeItemFromCart = (item) => (state) => {
  const { cart } = state;

  // Convertir a array mutable si es necesario
  const cartArray = cart.asMutable ? cart.asMutable() : Array.isArray(cart) ? cart : [];

  if (!Array.isArray(cartArray)) {
    console.warn('removeItemFromCart: cart is not an array, using empty array', cart);
    return { cart: [], state };
  }

  const hash = getHash(item);
  const [newCart] = findAndUpdate(
    (i) => {
      const itemHash = i.hash || getHash(i);
      return itemHash === hash;
    },
    (i) => ({ ...i, count: i.count - 1 }),
    cartArray
  );
  return { cart: newCart.filter((i) => i.count > 0), state };
};

export const deleteItemFromCart = (item) => (state) => {
  const { cart } = state;

  // Convertir a array mutable si es necesario
  const cartArray = cart.asMutable ? cart.asMutable() : Array.isArray(cart) ? cart : [];

  if (!Array.isArray(cartArray)) {
    console.warn('deleteItemFromCart: cart is not an array, returning empty array', cart);
    return { cart: [], state };
  }

  const hash = getHash(item);

  const filteredCart = cartArray.filter((i) => {
    const itemHash = i.hash || getHash(i);
    return itemHash !== hash;
  });
  return { cart: filteredCart, state };
};

export const modifyItemInCart = (item) => (state) => {
  const { cart } = state;

  // Asegurar que cart sea un array
  if (!Array.isArray(cart)) {
    console.warn('modifyItemInCart: cart is not an array, returning item with hash', cart);
    return { cart: [{ ...item, hash: getHash(item) }], state };
  }

  const [newCart] = findAndUpdate(
    (i) => i.hash === item.hash,
    () => ({ ...item, hash: getHash(item) }),
    cart
  );
  return { cart: newCart, state };
};

export const checkCartDenormalize =
  (address = null, discount = null, dispatchType = null) =>
  (state) => {
    const { cart, orderTaker } = state;
    return { cart: cart, state, address, discount, dispatchType, orderTaker };
  };

export const updateItemInCart = (oldItem, newItem) => (state) => {
  const { cart } = state;

  // Convertir a array mutable si es necesario
  const cartArray = cart.asMutable ? cart.asMutable() : Array.isArray(cart) ? cart : [];

  if (!Array.isArray(cartArray)) {
    console.warn('updateItemInCart: cart is not an array, returning new item', cart);
    return { cart: [{ ...newItem, hash: getHash(newItem) }], state };
  }

  const oldHash = getHash(oldItem);
  const newHash = getHash(newItem);

  // Si el hash cambió (modificadores diferentes), eliminar el viejo y agregar el nuevo
  if (oldHash !== newHash) {
    const filteredCart = cartArray.filter((i) => {
      const itemHash = i.hash || getHash(i);
      return itemHash !== oldHash;
    });
    const result = { cart: [...filteredCart, { ...newItem, hash: newHash }], state };
    return result;
  }

  // Si el hash es el mismo, solo actualizar la cantidad
  const [newCart] = findAndUpdate(
    (i) => {
      const itemHash = i.hash || getHash(i);
      return itemHash === oldHash;
    },
    () => ({ ...newItem, hash: newHash }),
    cartArray
  );

  const result = { cart: newCart, state };
  return result;
};

export const getLastDateHour = (date, businessHours = []) => {
  const dayHours = businessHours.filter((hour) => hour.dayInt === date.day());

  return (
    dayHours.length > 0 &&
    dayHours.reduce((lastHour, hour) => {
      if (!lastHour) {
        return hour.closingTime;
      }

      if (dayjs(hour.closingTime).isAfter(dayjs(lastHour))) {
        return hour.closingTime;
      }

      return lastHour.closingTime;
    })
  );
};

export const getFutureOrderDates = (
  futureDates,
  endDatetime,
  withoutStockUntil,
  businessHours,
  productAvailability,
  delay,
  index
) => {
  let newFutureOrderDates = futureDates;
  const date = dayjs().tz(intl.timezone).add(index, 'day');
  if (date.isSameOrBefore(dayjs(endDatetime)) || !endDatetime) {
    const lastDateHour = getLastDateHour(date, businessHours);

    if (
      productAvailability.length > 0 &&
      productAvailability.includes(date.day()) &&
      !newFutureOrderDates.availabilityDatetime &&
      (!withoutStockUntil || dayjs().tz(intl.timezone).add(index, 'day').isAfter(dayjs(withoutStockUntil)))
    ) {
      newFutureOrderDates = {
        ...newFutureOrderDates,
        availabilityDatetime: dayjs().tz(intl.timezone).add(index, 'day').startOf('day')
      };
    }

    if (lastDateHour) {
      date.hour(dayjs.utc(lastDateHour).format('HH'));
      date.minute(dayjs.utc(lastDateHour).format('mm')).subtract(delay, 'minutes');

      if (date.isAfter(newFutureOrderDates.lastDatetime)) {
        newFutureOrderDates = {
          ...newFutureOrderDates,
          lastDatetime: date
        };
      }
    }
  }
  return newFutureOrderDates;
};

export const getProductStock = (endDatetimeString, availability, withoutStockUntil, futureOrder) => {
  const endDatetime = endDatetimeString ? dayjs(endDatetimeString) : null;
  const futureOrderDate =
    futureOrder?.date && futureOrder?.time
      ? dayjs(`${futureOrder.date} ${futureOrder.time}`, 'YYYY-MM-DD HH:mm')
      : null;

  if (endDatetime && futureOrderDate && endDatetime.isSameOrBefore(futureOrderDate)) {
    return false;
  }

  const checkWithoutStockUntilDay =
    withoutStockUntil && dayjs().tz(intl.timezone).isAfter(dayjs(withoutStockUntil))
      ? dayjs().tz(intl.timezone).day()
      : false;

  const checkfutureOrderDateDay =
    futureOrderDate && (!withoutStockUntil || futureOrderDate.isAfter(dayjs(withoutStockUntil)))
      ? futureOrderDate.day()
      : false;

  const hasWithoutStockUntil = withoutStockUntil
    ? checkWithoutStockUntilDay
    : dayjs().tz(intl.timezone).day();

  const checkAvailability = futureOrderDate ? checkfutureOrderDateDay : hasWithoutStockUntil;

  const checkFutureOrderDate = futureOrderDate ? futureOrderDate.isAfter(dayjs(withoutStockUntil)) : false;

  return availability.length > 0 ? availability.includes(checkAvailability) : checkFutureOrderDate;
};

export const getBackToStockDate = (withoutStockUntil, futureOrderDates) => {
  const lastDate =
    withoutStockUntil &&
    futureOrderDates.availabilityDatetime &&
    futureOrderDates.availabilityDatetime.isBefore(dayjs(withoutStockUntil))
      ? dayjs(withoutStockUntil)
      : futureOrderDates.availabilityDatetime;

  const checkWithoutStockUntil = withoutStockUntil ? lastDate : futureOrderDates.availabilityDatetime;

  return futureOrderDates.availabilityDatetime ? checkWithoutStockUntil : dayjs(withoutStockUntil);
};

export const getStockStatus = (
  product,
  withoutStockUntil,
  futureOrder,
  endDatetime,
  productAvailability,
  futureOrderDates,
  futureOrdersEnabled,
  availableOnDaysString
) => {
  if (
    (!product.stock && !withoutStockUntil) ||
    (futureOrder &&
      dayjs(`${futureOrder.date} ${futureOrder.time}`, 'YYYY-MM-DD HH:mm').isAfter(dayjs(endDatetime)))
  ) {
    return { hasStock: false };
  }

  if (withoutStockUntil || productAvailability.length > 0) {
    return {
      hasStock: getProductStock(endDatetime, productAvailability, withoutStockUntil, futureOrder),
      backToStockDate: getBackToStockDate(withoutStockUntil, futureOrderDates),
      availableOnDaysString: availableOnDaysString || '',
      enableFutureOrder:
        futureOrdersEnabled &&
        (productAvailability.length > 0
          ? !!futureOrderDates.availabilityDatetime &&
            (!endDatetime || !withoutStockUntil || dayjs(withoutStockUntil).isBefore(dayjs(endDatetime)))
          : dayjs(withoutStockUntil).isBefore(futureOrderDates.lastDatetime))
    };
  }

  return { hasStock: true };
};
